// Landing page for Explore › Statistics — the index of all the recreated
// Handbook statistics pages, grouped by section. Built pages link through;
// not-yet-built pages show a "Coming soon" chip. Each ready tile shows a
// tiny chart thumbnail (#235) sourced from /api/explore/stats/hub-previews.

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardActionArea, CardContent, Chip,
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import axios from 'axios';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { STAT_SECTIONS } from './catalogue';
import StatThumbnail from './StatThumbnail';

// Stable anchor slug from a section heading.
const sectionAnchor = (s) => s.toLowerCase().replace(/\s+/g, '-');

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Fetch the per-slug preview payload once on mount. Tries the daily
// pre-cached static file first (#266 — backend writes
// `backend/static/hub-previews.json` daily via the
// `parlsearch-hub-previews-regen` systemd timer; FastAPI serves it from
// `/static`), then falls back to the live `/api/explore/stats/hub-previews`
// endpoint on any failure (non-2xx, network error, missing `previews` key).
// Loading state persists across both attempts so tiles don't flash an empty
// state during the fallback. Failures are silent — tiles still render with
// title + Coming-soon chip, just no thumbnail.
function usePreviews() {
  const [state, setState] = useState({ previews: null, loading: true,
                                          error: false });

  useEffect(() => {
    let cancelled = false;

    const finish = (previews, error) => {
      if (cancelled) return;
      setState({ previews: previews || {}, loading: false, error });
    };

    const fallback = () => {
      axios.get(`${API_URL}/api/explore/stats/hub-previews`)
        .then((response) => {
          const previews = (response.data && response.data.previews) || {};
          finish(previews, false);
        })
        .catch(() => finish({}, true));
    };

    axios.get(`${API_URL}/static/hub-previews.json`)
      .then((response) => {
        if (cancelled) return;
        const previews = response.data && response.data.previews;
        if (response.status === 200 && previews
            && Object.keys(previews).length > 0) {
          finish(previews, false);
        } else {
          fallback();
        }
      })
      .catch(() => {
        if (cancelled) return;
        fallback();
      });

    return () => { cancelled = true; };
  }, []);

  return state;
}

export default function ExploreStatsHub() {
  const total = STAT_SECTIONS.reduce((n, s) => n + s.pages.length, 0);
  const ready = STAT_SECTIONS.reduce(
    (n, s) => n + s.pages.filter((p) => p.ready).length, 0);

  const { previews, loading } = usePreviews();

  // When the URL carries a fragment (e.g. /explore/stats#sitting-patterns —
  // the shape the sidebar Statistics nav uses), scroll the matching section
  // into view after render. Re-runs on hash changes so clicking a different
  // sidebar item re-anchors without a full reload.
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <InsightsIcon color="primary" fontSize="large" />
        <Typography variant="h4">Explore Statistics</Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Interactive charts and summarised data about the Australian Parliament —
        committees, parties, sitting patterns, divisions, ministerial statements
        and parliamentarian demographics. ({ready} of {total} available)
      </Typography>

      {STAT_SECTIONS.map((sec) => (
        <Paper key={sec.section} id={sectionAnchor(sec.section)}
               elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3, scrollMarginTop: 16 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>{sec.section}</Typography>
          <Grid container spacing={2}>
            {sec.pages.map((p) => {
              const preview = previews ? previews[p.slug] : null;
              return (
                <Grid item xs={12} sm={6} md={4} key={p.slug}>
                  <Card variant="outlined" sx={{ height: '100%',
                                                  opacity: p.ready ? 1 : 0.6 }}>
                    {p.ready ? (
                      <CardActionArea component={RouterLink}
                                       to={`/explore/stats/${p.slug}`}
                                       sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            {p.title}
                          </Typography>
                          <StatThumbnail preview={preview}
                                          kind={p.thumbnailKind}
                                          loading={loading} />
                        </CardContent>
                      </CardActionArea>
                    ) : (
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                          {p.title}
                        </Typography>
                        <Chip label="Coming soon" size="small" />
                      </CardContent>
                    )}
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      ))}
    </Box>
  );
}
